import './input-checkbox.scss';

import { isArray, isEmpty } from 'lodash';
import {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { formatTestId } from '../../../data/utils/string.utils';
import { BaseInput } from './base-input';

export type InputCheckboxItem<T> = {
  key: T;
  title?: JSX.Element | string | number;
  hidden?: boolean;
  disabled?: boolean;
};

export enum InputCheckboxType {
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  TAG = 'tag',
}

export enum InputCheckboxDirection {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum InputCheckboxState {
  DETERMINATE = 'active',
  INDETERMINATE = 'semi-active',
}

type InputCheckboxProps<T> = Omit<
  ComponentPropsWithRef<typeof BaseInput>,
  'value' | 'onChange'
> & {
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  error?: string | { error: string; [key: string]: string };
  type?: InputCheckboxType;
  direction?: InputCheckboxDirection;
  value?: T[] | T;
  state?: InputCheckboxState;
  multiple?: boolean;
  disabled?: boolean;
  options?: InputCheckboxItem<T>[];
  placeholder?: string;
  onChange?: (value: T[] | T) => void;
};

export function InputCheckbox<T>({
  testId,
  className,
  label,
  description,
  error,
  type = InputCheckboxType.CHECKBOX,
  direction = InputCheckboxDirection.VERTICAL,
  value,
  state = InputCheckboxState.DETERMINATE,
  multiple = false,
  disabled = false,
  placeholder,
  onChange,
  ...props
}: InputCheckboxProps<T>) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-checkbox', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  const [selected, setSelected] = useState<T[]>([]);

  useEffect(() => {
    if (!isArray(value)) {
      setSelected([value as T]);
    } else {
      setSelected(value.filter(x => !isEmpty(x)) as T[]);
    }
  }, [value, props.name]);

  const onHandleChange = useCallback(
    (option: T) => {
      if (multiple) {
        if (type === InputCheckboxType.RADIO) {
          setSelected(selected.includes(option) ? [] : [option]);

          onChange?.(selected.includes(option) ? (null as T) : [option as T]);
        } else {
          const newSelected = [...selected];
          const position = newSelected.indexOf(option);

          if (position !== -1) {
            newSelected.splice(position, 1);
          } else {
            newSelected.push(option);
          }

          setSelected(newSelected.filter(x => !isEmpty(x)));

          onChange?.(newSelected.filter(x => !isEmpty(x)) as T[]);
        }
      } else if (type === InputCheckboxType.RADIO) {
        setSelected([option]);

        onChange?.(option as T);
      } else {
        setSelected(selected.includes(option) ? [] : [option]);

        onChange?.(selected.includes(option) ? (null as T) : (option as T));
      }
    },
    [multiple, selected, type, onChange]
  );

  const options = useMemo(
    () => (props.options ?? []).filter(option => option.hidden !== false),
    [props.options]
  );

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--check${
        direction !== InputCheckboxDirection.VERTICAL
          ? ` form-group--${direction}`
          : ``
      }${className ? ` form-group--${className}` : ''}${
        error ? ' invalid' : ''
      }${disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          {description}
        </div>
      )}

      <div className="input-group">
        {isEmpty(options)
          ? placeholder && <p>{placeholder}</p>
          : options.map(option => (
              <button
                key={option.key as string}
                data-test-id={getTestId(['option', option.key as string])}
                className={`form-check form-check--${type}${
                  selected.includes(option.key as T) ? ` ${state}` : ''
                }`}
                type="button"
                disabled={option.disabled ?? disabled}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();

                  onHandleChange(option.key);
                }}
              >
                <span
                  data-test-id={getTestId([
                    'option',
                    option.key as string,
                    'text',
                  ])}
                >
                  {option.title}
                </span>
              </button>
            ))}

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
